import { API_CONFIG, API_ENDPOINTS, TIMEOUTS } from '../constants/config';
import { BackendUser } from './auth';
import {
  createTokenRefreshInterceptor,
  getValidAccessToken,
  getAxiosAuthConfig,
  isCookieAuthMode,
} from '../utils/tokenInterceptor';
import { getToken } from '../utils/storage';

// Onboarding data interface
export interface OnboardingData {
  name: string; // Required - Display Name (not unique)
  age_bracket: string;
  industry: string;
  is_student?: boolean;
}

export interface OnboardingStatus {
  onboarding_completed: boolean;
  tour_completed?: boolean;
  age_bracket?: string;
  industry?: string;
  is_student?: boolean;
}

// Create axios instance with configuration
import axios from 'axios';

const api = axios.create({
  ...API_CONFIG,
  timeout: TIMEOUTS.API_REQUEST,
  ...getAxiosAuthConfig(),
});

// Add auth token to requests (skip when cookie mode - browser sends cookies)
api.interceptors.request.use(async (config) => {
  const token = await getValidAccessToken();
  if (token && token !== 'cookie') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add token refresh interceptor
createTokenRefreshInterceptor(api);

export const onboardingAPI = {
  /**
   * Complete user onboarding
   * @param data - Onboarding form data
   * @param accessToken - Optional explicit token (bypasses storage/interceptor when provided)
   */
  complete: async (
    data: OnboardingData,
    accessToken?: string | null
  ): Promise<BackendUser> => {
    // Prefer explicit token, then storage - never use axios (its interceptor can clear session on 401)
    const token = accessToken ?? (await getToken('auth_token'));

    const useCookies = isCookieAuthMode();
    if (!token && !useCookies) {
      throw new Error(
        'No authentication token. Please sign in again to complete setup.'
      );
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_CONFIG.baseURL}/onboarding/complete`, {
      method: 'POST',
      headers,
      credentials: useCookies ? 'include' : 'same-origin',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData?.detail || `Request failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get onboarding status
   */
  getStatus: async (): Promise<OnboardingStatus> => {
    const response = await api.get<OnboardingStatus>(
      API_ENDPOINTS.ONBOARDING.STATUS
    );
    return response.data;
  },

  /**
   * Mark app tour as completed (for "new user" hybrid persistence).
   * Backend may not implement this yet; call is safe to fail.
   */
  completeTour: async (): Promise<void> => {
    try {
      await api.post(API_ENDPOINTS.ONBOARDING.TOUR_COMPLETE, {});
    } catch {
      // Backend route is optional; ignore 404/other errors so UI isn't noisy
    }
  },
};
